import { z } from "zod";

/**
 * Supported worker languages for site-induction content (issue #991).
 *
 * The set is the languages most commonly spoken on UK construction sites.
 * English is the authoritative record everywhere; every other locale is a
 * translation layered on top (AI-generated + cached, builder-reviewable).
 *
 * This module is the single wire-level source of truth for locale codes —
 * acknowledgement payloads, translation rows and the worker-facing pickers all
 * validate against it. The per-locale UI string tables live in
 * `@thesitebook/strings` (`inductionWorkerStrings`); a unit test keeps the two
 * key sets in lockstep so they cannot drift.
 */
export const INDUCTION_LOCALES = [
  { code: "en", label: "English", nativeLabel: "English" },
  { code: "pl", label: "Polish", nativeLabel: "Polski" },
  { code: "ro", label: "Romanian", nativeLabel: "Română" },
  { code: "lt", label: "Lithuanian", nativeLabel: "Lietuvių" },
  { code: "bg", label: "Bulgarian", nativeLabel: "Български" },
  { code: "ru", label: "Russian", nativeLabel: "Русский" },
  { code: "uk", label: "Ukrainian", nativeLabel: "Українська" },
  { code: "pt", label: "Portuguese", nativeLabel: "Português" },
  { code: "es", label: "Spanish", nativeLabel: "Español" },
] as const;

export type InductionLocaleInfo = (typeof INDUCTION_LOCALES)[number];

export const INDUCTION_LOCALE_CODES = INDUCTION_LOCALES.map(
  (locale) => locale.code,
) as [InductionLocaleInfo["code"], ...InductionLocaleInfo["code"][]];

export const InductionLocaleSchema = z.enum(INDUCTION_LOCALE_CODES);

export type InductionLocale = z.infer<typeof InductionLocaleSchema>;

/** Locales that need a translation generated (everything except English). */
export const TRANSLATABLE_INDUCTION_LOCALES = INDUCTION_LOCALES.filter(
  (locale) => locale.code !== "en",
);

export function isInductionLocale(value: unknown): value is InductionLocale {
  return InductionLocaleSchema.safeParse(value).success;
}
